import React, { useState, useEffect, useCallback } from 'react';
import {
    Cpu,
    Copy,
    Trash2,
    RefreshCw,
    Clock,
    HardDrive,
    AlertTriangle,
    CheckCircle2,
    Loader2,
    Layout
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './AdminPages.css';

function AdminActiveDevices() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [killing, setKilling] = useState(null);
    const [confirmKill, setConfirmKill] = useState(null);
    const { showToast } = useToast();

    const fetchDevices = useCallback(async () => {
        try {
            const response = await adminApi.getDevices();
            setDevices(response.data.devices);
        } catch (error) {
            console.error('Fetch devices error:', error);
            showToast('Failed to fetch active devices', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchDevices();
        const interval = setInterval(fetchDevices, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, [fetchDevices]);

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
        showToast('Device ID copied to clipboard');
    };

    const handleKillDevice = async (deviceId) => {
        setKilling(deviceId);
        try {
            await adminApi.killDevice(deviceId);
            showToast('Termination triggered for emulator');
            setConfirmKill(null);
            fetchDevices();
        } catch (error) {
            console.error('Kill device error:', error);
            showToast(error.response?.data?.error || 'Failed to terminate device', 'error');
        } finally {
            setKilling(null);
        }
    };

    const formatUptime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h`;
        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
        return `${seconds}s`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="admin-page active-devices-page">
            <div className="admin-dashboard-header">
                <div>
                    <h1>Active Devices</h1>
                    <p className="subtitle">Global management of virtualization instances across all workspaces.</p>
                </div>
                <div className="admin-header-actions">
                    <button className="admin-cancel-btn" onClick={() => { setLoading(true); fetchDevices(); }}>
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} style={{ marginRight: '8px' }} />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            <div className="admin-card" style={{ marginTop: '24px' }}>
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Device ID</th>
                                <th>Workspace</th>
                                <th>Status</th>
                                <th>Platform & Profile</th>
                                <th>Installed App</th>
                                <th>Created At</th>
                                <th>Uptime</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && devices.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="table-loading">
                                        <Loader2 className="animate-spin" />
                                        <span>Loading devices...</span>
                                    </td>
                                </tr>
                            ) : devices.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="empty-state">
                                        <Cpu size={48} />
                                        <p>No active devices found across any workspace.</p>
                                    </td>
                                </tr>
                            ) : (
                                devices.map((device) => (
                                    <tr key={device.id}>
                                        <td className="id-cell">
                                            <div className="flex-row">
                                                <code title={device.id}>{device.id.slice(0, 8)}...</code>
                                                <button className="btn btn-sm" onClick={() => handleCopyId(device.id)}>
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="workspace-info">

                                                <span className="workspace-name">{device.workspace?.name || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${device.status === 'AVAILABLE' ? 'success' : 'warning'}`}>
                                                {device.status === 'AVAILABLE' && <CheckCircle2 size={12} />}
                                                {device.status === 'PROVISIONING' && <Loader2 size={12} className="animate-spin" />}
                                                {device.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="profile-column">
                                                <span className="platform-label">{device.platform}</span>
                                                <span className="profile-value">{device.deviceProfile}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="app-cell">
                                                <HardDrive size={14} />
                                                <span title={device.installedApp}>{device.installedApp}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="date-text">{formatDate(device.createdAt)}</span>
                                        </td>
                                        <td>
                                            <div className="uptime-column">
                                                <Clock size={14} />
                                                <span>{formatUptime(device.uptimeMs)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className="icon-btn delete"
                                                title="Kill Device"
                                                onClick={() => setConfirmKill(device.id)}
                                                disabled={killing === device.id}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Confirmation Modal */}
            {confirmKill && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h3>Force Terminate Emulator?</h3>
                            <button className="close-btn" onClick={() => setConfirmKill(null)}>&times;</button>
                        </div>
                        <div className="admin-modal-body">
                            <p>Are you sure you want to kill this emulator instance?</p>
                            <div className="modal-warning-box" style={{ marginTop: '16px', padding: '12px', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid #ef4444', borderRadius: '4px' }}>
                                <p style={{ margin: 0, color: '#ef4444', fontSize: '0.85rem' }}>
                                    <strong>Warning:</strong> This will immediately end any tests running on this device. The physical cloud instance will be destroyed.
                                </p>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-cancel-btn" onClick={() => setConfirmKill(null)}>Cancel</button>
                            <button
                                className="admin-cancel-btn"
                                style={{ backgroundColor: '#ef4444', color: 'white' }}
                                onClick={() => handleKillDevice(confirmKill)}
                                disabled={killing !== null}
                            >
                                {killing ? 'Terminating...' : 'Confirm Termination'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminActiveDevices;
